import type { WidgetEvent } from '@kunzite/shared-types';

export interface WidgetEventsClientDeps {
  url: string;
  jwt: string;
  onEvent: (event: WidgetEvent) => void;
  /** override for tests */
  WebSocket?: typeof globalThis.WebSocket;
  backoffMs?: number[];
}

interface SocketLike {
  readonly readyState: number;
  onopen: ((e?: unknown) => void) | null;
  onclose: ((e: { code: number; reason: string }) => void) | null;
  onmessage: ((e: { data: string }) => void) | null;
  onerror: ((e: unknown) => void) | null;
  close(): void;
}

export class WidgetEventsClient {
  private ws: SocketLike | null = null;
  private retryIdx = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private readonly backoff: number[];

  constructor(private readonly deps: WidgetEventsClientDeps) {
    this.backoff = deps.backoffMs ?? [1_000, 2_000, 5_000, 10_000, 30_000];
  }

  connect(): void {
    this.stopped = false;
    this.open();
  }

  disconnect(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.ws?.close();
    this.ws = null;
  }

  private open(): void {
    const Ctor = this.deps.WebSocket ?? globalThis.WebSocket;
    const url = `${this.deps.url}?token=${encodeURIComponent(this.deps.jwt)}`;
    const ws = new Ctor(url) as unknown as SocketLike;
    ws.onopen = () => {
      this.retryIdx = 0;
    };
    ws.onclose = (_e) => {
      if (this.stopped) return;
      const delay = this.backoff[Math.min(this.retryIdx, this.backoff.length - 1)] ?? 30_000;
      this.retryIdx++;
      this.timer = setTimeout(() => this.open(), delay);
    };
    ws.onmessage = (e) => {
      try {
        const env = JSON.parse(e.data) as { payload: WidgetEvent };
        if (env.payload && typeof env.payload.event === 'string') {
          this.deps.onEvent(env.payload);
        }
      } catch {
        // malformed message, ignore
      }
    };
    ws.onerror = () => undefined;
    this.ws = ws;
  }
}
